/**
 * AbilityContext
 * Provides CASL ability instance based on current user and clinic
 */

import { createContext, useContext, useMemo } from 'react';
import { createContextualCan } from '@casl/react';
import { useAuth } from './AuthContext';
import { defineAbilitiesFor } from '../abilities/defineAbilities';

// Create the context
const AbilityContext = createContext();

// Create the Can component bound to AbilityContext
export const Can = createContextualCan(AbilityContext.Consumer);

/**
 * AbilityProvider - Wraps the app and provides ability instance
 */
export function AbilityProvider({ children }) {
  const { user, currentClinic } = useAuth();

  // Compute ability based on current user and clinic
  const ability = useMemo(() => {
    if (!user || !currentClinic) {
      // Return an ability with no permissions if no user/clinic
      return defineAbilitiesFor(null, null);
    }

    return defineAbilitiesFor(user, currentClinic.id);
  }, [user, currentClinic]);

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
}

/**
 * useAbility hook - Access ability instance in components
 */
export function useAbility() {
  const context = useContext(AbilityContext);
  if (context === undefined) {
    throw new Error('useAbility must be used within an AbilityProvider');
  }
  return context;
}

export default AbilityContext;
